import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Input, Tag, Modal, Form, Select, message, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { applicationService, Application } from '../services/application';

const Applications: React.FC = () => {
  const [data, setData] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [search]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response: any = await applicationService.getList(search);
      setData(response);
    } catch (error) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingApp(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Application) => {
    setEditingApp(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await applicationService.delete(id);
      message.success('删除成功');
      loadData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingApp) {
        await applicationService.update(editingApp.id, values);
        message.success('更新成功');
      } else {
        await applicationService.create(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string) => <Tag>{platform}</Tag>,
    },
    {
      title: '标识符',
      dataIndex: 'identifier',
      key: 'identifier',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: any = {
          Active: 'green',
          Development: 'blue',
          Deprecated: 'red',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      },
    },
    {
      title: '版本数',
      key: 'versions',
      render: (record: Application) => (record as any).versions?.length || 0,
    },
    {
      title: '创建人',
      dataIndex: 'createdByName',
      key: 'createdByName',
      render: (name: string) => name || '-',
    },
    {
      title: '最后更新人',
      dataIndex: 'updatedByName',
      key: 'updatedByName',
      render: (name: string) => name || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Application) => (
        <Space>
          <Button
            type="link"
            onClick={() => navigate(`/applications/${record.id}/versions`)}
          >
            管理版本
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除此应用吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>软件管理</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建应用
        </Button>
      </div>

      <Input
        placeholder="搜索应用..."
        prefix={<SearchOutlined />}
        style={{ marginBottom: 16, width: 300 }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
      />

      <Modal
        title={editingApp ? '编辑应用' : '创建新应用'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="应用名称"
            rules={[{ required: true, message: '请输入应用名称' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="identifier"
            label="包名/标识符 (ID)"
            rules={[{ required: true, message: '请输入标识符' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="platform"
            label="平台"
            rules={[{ required: true, message: '请选择平台' }]}
          >
            <Select>
              <Select.Option value="iOS">iOS</Select.Option>
              <Select.Option value="Android">Android</Select.Option>
              <Select.Option value="Windows">Windows</Select.Option>
              <Select.Option value="macOS">macOS</Select.Option>
              <Select.Option value="Linux">Linux</Select.Option>
              <Select.Option value="Web">Web</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              <Select.Option value="Development">开发中 (Development)</Select.Option>
              <Select.Option value="Active">活跃 (Active)</Select.Option>
              <Select.Option value="Deprecated">已废弃 (Deprecated)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="description" label="描述">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Applications;
